import {
  type NetPriceData,
  type PriceData,
  type TotalPriceData,
  type WithSurcharge,
} from "~/domains/pricing/types/PriceData";
import { formatCurrency } from "~/common/utils/currencyFormatter";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { type ReactNode } from "react";
import { getProductData, type ProductData } from "~/domains/product/types/ProductData";

import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

/**
 * This sum is volatile and if any element has a value of 0, the sum will be 0
 * as requested in comments in * https://agile.deloitte.de/browse/SCHAEFF-2295
 * @param {PriceData} prices
 * @returns {PriceData}
 */
export function volatileSum(...prices: PriceData[]): PriceData {
  // if any of the prices is zero, instead of summing, we return 0 as well
  if (prices.some((price) => price.value === 0)) {
    return zero();
  }

  return sum(...prices);
}

export function sum(...prices: PriceData[]): PriceData {
  if (prices.length === 0) {
    return zero();
  }

  // if for some reason we are summing different currencies, this is an error and return zero too
  const currencyIso = prices[0].currencyIso;
  if (prices.some((price) => price.currencyIso !== currencyIso)) {
    console.warn("Tried summing prices with different currencies");
    return zero();
  }

  const total = prices.reduce((accumulator, price) => accumulator + price.value, 0);

  return {
    value: total,
    currencyIso,
  };
}

export function multiply(price: PriceData, multiplier: number): PriceData {
  const value = price.value * multiplier;
  const currencyIso = price.currencyIso;
  return {
    value,
    currencyIso,
  };
}

const zeroValueBase = () => ({ value: 0, currencyIso: getRuntimeConfig().currencyIso });

export function zero(): PriceData {
  return zeroValueBase();
}

function resolveIsSubscription(x: ProductData | ProductDescriptor | boolean): boolean {
  return typeof x === "boolean" ? x : !getProductData(x).quantityVisible;
}
export function zeroNetPrice(productOrIsSubscription: ProductData | ProductDescriptor | boolean): NetPriceData {
  return {
    ...zeroValueBase(),
    unit: 1,
    surcharge: 0,
    isSubscription: resolveIsSubscription(productOrIsSubscription),
    baseValue: 0,
  };
}
export function zeroTotalPrice(
  productOrIsSubscription: ProductData | ProductDescriptor | boolean,
): TotalPriceData & WithSurcharge {
  return {
    ...zeroValueBase(),
    surcharge: 0,
    isSubscription: resolveIsSubscription(productOrIsSubscription),
    baseValue: 0,
  };
}

export function text(price: PriceData, zeroText: string = "-"): string {
  if (price.value === 0) {
    return zeroText;
  }
  return nonVolatileText(price);
}

export function text2(price: PriceData, zeroText: ReactNode): ReactNode {
  if (price.value === 0) {
    return zeroText;
  }
  return nonVolatileText(price);
}

export function nonVolatileText(price: PriceData): string {
  return formatCurrency(price.value, price.currencyIso);
}

export function lower(price?: PriceData, otherPrice?: PriceData) {
  if (!price || price.value <= 0) {
    return otherPrice;
  }
  if (!otherPrice || otherPrice.value <= 0) {
    return price;
  }
  return price.value < otherPrice.value ? price : otherPrice;
}
